/**
 * Minimal syntax highlighter for the Docs page.
 *
 * Uses highlight.js *core* and registers only the languages the docs actually
 * use (python, typescript, bash, json). Importing from `highlight.js/lib/core`
 * + per-language modules lets Vite tree-shake the ~190 unused grammars, keeping
 * the bundle small.
 *
 * The light `github` theme CSS is imported once in `main.ts` to match the
 * paper-white canvas (DESIGN.md). `style.css` overrides the theme's `.hljs`
 * background to transparent so the surrounding `<pre>` provides the surface.
 */
import hljs from 'highlight.js/lib/core';
import python from 'highlight.js/lib/languages/python';
import typescript from 'highlight.js/lib/languages/typescript';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';

hljs.registerLanguage('python', python);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('json', json);

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Return highlighted HTML for `code`. When `language` is unknown/omitted the
 * source is HTML-escaped and returned verbatim (no highlighting). hljs escapes
 * its own output, so the result is always safe to inject via `v-html`.
 */
export function highlightCode(code: string, language?: string): string {
  if (language && hljs.getLanguage(language)) {
    try {
      return hljs.highlight(code, { language, ignoreIllegals: true }).value;
    } catch {
      // fall through to plain escaped output
    }
  }
  return escapeHtml(code);
}
